import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Badge,
  Tooltip,
  Card,
  CardHeader,
  CardPreview,
} from '@fluentui/react-components';
import {
  LinkRegular,
  OpenRegular,
  ClockRegular,
  DocumentTextRegular,
} from '@fluentui/react-icons';
import { ExtractedLink } from '../utils/linkExtractor';
import { getExtractedMetadata, useMetadataUpdates } from './LinkPrefetcher';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    height: '100%',
    overflow: 'auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground2,
    height: '100%',
  },
  linkCard: {
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    ':hover': {
      boxShadow: tokens.shadow8,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  linkHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
  },
  linkContent: {
    padding: tokens.spacingVerticalS,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  linkTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase300,
    wordBreak: 'break-word',
  },
  linkUrl: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontFamily: tokens.fontFamilyMonospace,
    wordBreak: 'break-all',
  },
  linkContext: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    lineHeight: '1.4',
  },
  linkMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
  },
  domainGroup: {
    marginBottom: tokens.spacingVerticalM,
  },
  domainHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  domainTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  linkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    marginLeft: 'auto',
  },
  statsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
});

interface ConversationSourcesProps {
  links: ExtractedLink[];
  onLinkClick?: (link: ExtractedLink) => void;
  groupByDomain?: boolean;
  showMedicalOnly?: boolean;
}

export const ConversationSources: React.FC<ConversationSourcesProps> = ({
  links,
  onLinkClick,
  groupByDomain = true,
  showMedicalOnly = false,
}) => {
  const classes = useStyles();
  const [localLinks, setLocalLinks] = useState<ExtractedLink[]>(links);
  
  // Subscribe to metadata updates to ensure re-rendering when prefetch completes
  useMetadataUpdates();
  
  // Listen for metadata extraction events
  useEffect(() => {
    const handleMetadataExtracted = (event: CustomEvent) => {
      const { url, title, description } = event.detail;
      setLocalLinks(prevLinks => 
        prevLinks.map(link => {
          if (link.url === url) {
            return {
              ...link,
              title: title || link.title,
              description: description || link.description
            };
          }
          return link;
        })
      );
    };
    
    // Add event listener for metadata extraction
    document.addEventListener('metadataExtracted', handleMetadataExtracted as EventListener);
    
    return () => {
      document.removeEventListener('metadataExtracted', handleMetadataExtracted as EventListener);
    };
  }, []);
  
  // Update local links when props change
  useEffect(() => {
    // Enrich links with any prefetched metadata
    const enrichedLinks = links.map(link => {
      const metadata = getExtractedMetadata(link.url);
      return {
        ...link,
        title: link.title || metadata.title || undefined,
        description: link.description || metadata.description || undefined
      };
    });
    setLocalLinks(enrichedLinks);
  }, [links]);

  // Filter links if needed
  const filteredLinks = showMedicalOnly 
    ? localLinks.filter(link => {
        try {
          const domain = new URL(link.url).hostname.toLowerCase();
          const medicalDomains = [
            'nih.gov', 'cdc.gov', 'who.int', 'fda.gov', 'cms.gov',
            'medlineplus.gov', 'pubmed.ncbi.nlm.nih.gov', 'ncbi.nlm.nih.gov',
            'webmd.com', 'mayoclinic.org', 'clevelandclinic.org', 'uptodate.com'
          ];
          return medicalDomains.some(medDomain => 
            domain.includes(medDomain) || domain.endsWith(medDomain)
          );
        } catch {
          return false;
        }
      })
    : localLinks;

  if (filteredLinks.length === 0) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <LinkRegular style={{ fontSize: '32px' }} />
          <Text size={400}>No Conversation Sources</Text>
          <Text size={300}>
            Links from AI responses will appear here for easy access
          </Text>
        </div>
      </div>
    );
  }

  const handleLinkClick = (link: ExtractedLink, event: React.MouseEvent) => {
    event.preventDefault();
    if (onLinkClick) {
      onLinkClick(link);
    } else {
      // Default behavior - open in new tab
      window.open(link.url, '_blank', 'noopener,noreferrer');
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Unknown';
    }
  };

  const getDomainFromUrl = (url: string): string => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return 'Unknown';
    }
  };

  const renderLink = (link: ExtractedLink, index: number) => {
    // Try to get the most up-to-date metadata
    const metadata = getExtractedMetadata(link.url);
    const title = link.title || metadata.title || getDomainFromUrl(link.url);
    const description = link.description || metadata.description;
    
    // Special handling for Medicare LCD/NCD URLs
    const isMedicareDocument = link.url.includes('cms.gov') && 
                              (link.url.includes('lcd.aspx') || link.url.includes('ncd.aspx'));
    
    return (
      <Card
        key={`${link.url}-${index}`}
        className={classes.linkCard}
        onClick={(event) => handleLinkClick(link, event)}
      >
        <CardHeader className={classes.linkHeader}>
          <LinkRegular />
          {isMedicareDocument && (
            <Badge appearance="filled" color="brand" size="small" style={{ marginLeft: tokens.spacingHorizontalS }}>
              {link.url.includes('lcd.aspx') ? 'LCD' : 'NCD'}
            </Badge>
          )}
          <div className={classes.linkActions}>
            <Tooltip content="Open in new tab" relationship="label">
              <Button
                appearance="subtle"
                icon={<OpenRegular />}
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(link.url, '_blank', 'noopener,noreferrer');
                }}
              />
            </Tooltip>
          </div>
        </CardHeader>
        <CardPreview className={classes.linkContent}>
          <div className={classes.linkTitle}>
            {title}
          </div>
          {description && (
            <div style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground1 }}>
              {description}
            </div>
          )}
          <div className={classes.linkUrl}>
            {link.url}
          </div>
          {link.context && (
            <div className={classes.linkContext}>
              "{link.context}"
            </div>
          )}
          <div className={classes.linkMeta}>
            <ClockRegular />
            <Text size={100}>
              Message {link.messageIndex + 1} • {formatTimestamp(link.timestamp)}
            </Text>
          </div>
        </CardPreview>
      </Card>
    );
  };

  if (!groupByDomain) {
    return (
      <div className={classes.container}>
        <div className={classes.statsContainer}>
          <Badge appearance="tint" icon={<LinkRegular />}>
            {filteredLinks.length} sources
          </Badge>
          <Badge appearance="outline" icon={<DocumentTextRegular />}>
            From conversation
          </Badge>
        </div>
        {filteredLinks.map(renderLink)}
      </div>
    );
  }

  // Group links by domain
  const groupedLinks = new Map<string, ExtractedLink[]>();
  filteredLinks.forEach(link => {
    const domain = getDomainFromUrl(link.url);
    if (!groupedLinks.has(domain)) {
      groupedLinks.set(domain, []);
    }
    groupedLinks.get(domain)!.push(link);
  });

  // Sort domains by number of links
  const sortedDomains = Array.from(groupedLinks.entries())
    .sort(([, aLinks], [, bLinks]) => bLinks.length - aLinks.length);

  return (
    <div className={classes.container}>
      <div className={classes.statsContainer}>
        <Badge appearance="tint" icon={<LinkRegular />}>
          {filteredLinks.length} sources
        </Badge>
        <Badge appearance="outline" icon={<DocumentTextRegular />}>
          {sortedDomains.length} domains
        </Badge>
      </div>
      
      {sortedDomains.map(([domain, domainLinks]) => (
        <div key={domain} className={classes.domainGroup}>
          <div className={classes.domainHeader}>
            <div className={classes.domainTitle}>
              {domain}
            </div>
            <Badge appearance="outline" size="small">
              {domainLinks.length}
            </Badge>
          </div>
          {domainLinks.map((link, index) => renderLink(link, index))}
        </div>
      ))}
    </div>
  );
};

export default ConversationSources;